import { rectsIntersect, type RectLike } from './utils';

const RANGE_PADDING = 8;

type IntlSegment = {
  index: number;
  segment: string;
};

export type CharacterRectCacheEntry = {
  rects: RectLike[];
  scrollX: number;
  scrollY: number;
  textContent: string;
};

type IntlWithSegmenter = typeof Intl & {
  Segmenter?: new (
    locales?: string | string[],
    options?: { granularity?: 'grapheme' | 'word' | 'sentence' }
  ) => {
    segment(input: string): Iterable<IntlSegment>;
  };
};

const toPlainRect = (rect: DOMRectReadOnly): RectLike => ({
  top: rect.top,
  right: rect.right,
  bottom: rect.bottom,
  left: rect.left,
  width: rect.width,
  height: rect.height,
});

const expandRect = (rect: RectLike, padding: number): RectLike => ({
  top: rect.top - padding,
  right: rect.right + padding,
  bottom: rect.bottom + padding,
  left: rect.left - padding,
  width: rect.width + padding * 2,
  height: rect.height + padding * 2,
});

const getGraphemeSegments = (value: string) => {
  const Segmenter = (Intl as IntlWithSegmenter).Segmenter;

  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: 'grapheme' });
    const segments = Array.from(segmenter.segment(value));

    return segments.map((segment, index) => ({
      value: segment.segment,
      start: segment.index,
      end: segments[index + 1]?.index ?? value.length,
    }));
  }

  const chars = Array.from(value);
  let cursor = 0;

  return chars.map((char) => {
    const start = cursor;
    cursor += char.length;

    return {
      value: char,
      start,
      end: cursor,
    };
  });
};

const isElementVisible = (element: HTMLElement) => {
  let current: HTMLElement | null = element;

  while (current) {
    const style = window.getComputedStyle(current);
    const opacity = Number.parseFloat(style.opacity || '1');

    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      opacity <= 0.05
    ) {
      return false;
    }

    current = current.parentElement;
  }

  return true;
};

export const collectVisibleTextNodes = (
  root: HTMLElement,
  excludedRoot: HTMLElement
) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    const textNode = currentNode as Text;
    const parent = textNode.parentElement;
    const tagName = parent?.tagName;

    if (
      parent &&
      !excludedRoot.contains(parent) &&
      !parent.closest('[data-name-overlap-ignore="true"]') &&
      textNode.nodeValue?.trim() &&
      tagName !== 'SCRIPT' &&
      tagName !== 'STYLE' &&
      tagName !== 'NOSCRIPT' &&
      isElementVisible(parent)
    ) {
      textNodes.push(textNode);
    }

    currentNode = walker.nextNode();
  }

  return textNodes;
};

export const getCharacterClientRects = (
  textNode: Text,
  cache: WeakMap<Text, CharacterRectCacheEntry>
) => {
  const textContent = textNode.textContent ?? '';

  if (!textContent || typeof document.createRange !== 'function') {
    return [] as RectLike[];
  }

  const cached = cache.get(textNode);

  if (
    cached &&
    cached.textContent === textContent &&
    cached.scrollX === window.scrollX &&
    cached.scrollY === window.scrollY
  ) {
    return cached.rects;
  }

  const range = document.createRange();
  const rects: RectLike[] = [];

  try {
    getGraphemeSegments(textContent).forEach((segment) => {
      if (!segment.value.trim()) {
        return;
      }

      range.setStart(textNode, segment.start);
      range.setEnd(textNode, segment.end);

      Array.from(range.getClientRects())
        .map((rect) => toPlainRect(rect))
        .filter((rect) => rect.width > 0 && rect.height > 0)
        .forEach((rect) => {
          rects.push(rect);
        });
    });
  } catch (error) {
    return [];
  } finally {
    if (typeof range.detach === 'function') {
      range.detach();
    }
  }

  cache.set(textNode, {
    rects,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    textContent,
  });

  return rects;
};

export const getCandidateCharacterRects = (
  textNodes: Text[],
  titleRect: RectLike,
  cache: WeakMap<Text, CharacterRectCacheEntry>,
  padding = RANGE_PADDING
) => {
  const expandedTitleRect = expandRect(titleRect, padding);
  const candidateRects: RectLike[] = [];

  textNodes.forEach((textNode) => {
    const parent = textNode.parentElement;

    if (!parent || !parent.isConnected || !isElementVisible(parent)) {
      return;
    }

    const parentRect = toPlainRect(parent.getBoundingClientRect());

    if (!rectsIntersect(parentRect, expandedTitleRect)) {
      return;
    }

    getCharacterClientRects(textNode, cache)
      .filter((rect) => rectsIntersect(rect, expandedTitleRect))
      .forEach((rect) => {
        candidateRects.push(rect);
      });
  });

  return candidateRects;
};
